import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { MeasuredChart } from '@/components/admin/MeasuredChart';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { 
  Play, 
  TrendingUp, 
  Target, 
  Award,
  ArrowRight,
  BookOpen,
  Sparkles,
  MessageCircle,
  Volume2,
  Star,
  Trophy,
  CircleCheck,
  CalendarCheck,
  Lock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [achievements, setAchievements] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, scenariosRes, achievementsRes] = await Promise.all([
        axios.get(`${API}/users/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/scenarios`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/achievements`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null)
      ]);
      setStats(statsRes.data);
      setScenarios(scenariosRes.data);
      if (achievementsRes) setAchievements(achievementsRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      // If scenarios don't exist, seed them
      if (error.response?.status === 404 || scenarios.length === 0) {
        try {
          await axios.post(`${API}/seed`);
          const scenariosRes = await axios.get(`${API}/scenarios`, { headers: { Authorization: `Bearer ${token}` } });
          setScenarios(scenariosRes.data);
        } catch (seedError) {
          console.error('Failed to seed scenarios:', seedError);
        }
      }
    }
    setLoading(false);
  };

  const getRecommendedScenario = () => {
    if (!scenarios.length) return null;
    // Return first scenario matching user's primary goal, or first available
    const goalMatch = scenarios.find(s => s.category === user?.primary_goal);
    return goalMatch || scenarios[0];
  };

  const categoryColors = {
    spouse: 'bg-pink-100 text-pink-600',
    kids: 'bg-blue-100 text-blue-600',
    parents: 'bg-purple-100 text-purple-600',
    retirement: 'bg-amber-100 text-amber-600',
  };

  const ACHIEVEMENT_ICONS = {
    'message-circle': MessageCircle,
    'volume-2': Volume2,
    'award': Award,
    'star': Star,
    'trophy': Trophy,
    'circle-check': CircleCheck,
    'calendar-check': CalendarCheck,
    'trending-up': TrendingUp,
  };

  const recommended = getRecommendedScenario();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00E085]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard">
        {/* Welcome Section with Clear Next Step */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#111827]">
              Welcome back, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-gray-600 mt-1">
              {stats?.sessions_completed === 0 
                ? "Start your first practice session today"
                : stats?.avg_score < 70 
                  ? "Keep practicing to improve your score"
                  : "Great progress! Try a harder scenario"}
            </p>
          </div>
          <Link to="/scenarios">
            <Button className="bg-[#00E085] text-[#00331E] hover:bg-[#00C978] rounded-full px-6" data-testid="start-practice-btn">
              <Play className="w-4 h-4 mr-2" />
              {stats?.sessions_completed === 0 ? "Start First Practice" : "Practice Now"}
            </Button>
          </Link>
        </div>

        {/* YOUR NEXT STEP - Resolution Engine Guidance */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6" data-testid="next-step-guidance">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#111827] mb-1">Your Next Step</h3>
              {stats?.sessions_completed === 0 ? (
                <>
                  <p className="text-gray-700 mb-3">Complete your first practice session to get personalized feedback</p>
                  <Link to="/scenarios">
                    <Button size="sm" className="bg-amber-500 text-white hover:bg-amber-600 rounded-full">
                      Choose a Scenario <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </>
              ) : stats?.avg_score < 60 ? (
                <>
                  <p className="text-gray-700 mb-3">Focus on <strong>stating your needs clearly</strong> at the start of conversations</p>
                  <Link to="/scenarios">
                    <Button size="sm" className="bg-amber-500 text-white hover:bg-amber-600 rounded-full">
                      Practice &ldquo;I statements&rdquo; <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </>
              ) : stats?.avg_score < 80 ? (
                <>
                  <p className="text-gray-700 mb-3">Work on <strong>guiding conversations toward resolution</strong> - end with clear next steps</p>
                  <Link to="/scenarios">
                    <Button size="sm" className="bg-amber-500 text-white hover:bg-amber-600 rounded-full">
                      Practice Resolution <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <p className="text-gray-700 mb-3">Excellent progress! Challenge yourself with a <strong>higher difficulty</strong> scenario</p>
                  <Link to="/scenarios">
                    <Button size="sm" className="bg-amber-500 text-white hover:bg-amber-600 rounded-full">
                      Try Advanced Scenario <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Quick Start Card */}
        {recommended && (
          <div className="bg-gradient-to-r from-[#00E085]/10 to-[#00E085]/5 rounded-3xl p-8 relative overflow-hidden" data-testid="quick-start-card">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#00E085]/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-[#00E085]" />
                <span className="text-sm font-medium text-[#00331E]">Recommended for you</span>
              </div>
              <h2 className="text-2xl font-bold text-[#111827] mb-2">{recommended.title}</h2>
              <p className="text-gray-600 mb-6 max-w-xl">{recommended.description}</p>
              <div className="flex items-center gap-4">
                <Link to={`/scenario/${recommended.id}`}>
                  <Button className="bg-[#111827] text-white hover:bg-[#1F2937] rounded-full px-6" data-testid="continue-scenario-btn">
                    Continue Practice
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${categoryColors[recommended.category]}`}>
                  {recommended.category}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl p-6 border border-gray-100" data-testid="stat-sessions">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-[#00E085]/20 rounded-xl flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-[#00E085]" />
              </div>
            </div>
            <p className="text-3xl font-bold text-[#111827]">{stats?.sessions_completed || 0}</p>
            <p className="text-gray-500 text-sm">Sessions Completed</p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-gray-100" data-testid="stat-avg-score">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-[#6366F1]/20 rounded-xl flex items-center justify-center">
                <Target className="w-5 h-5 text-[#6366F1]" />
              </div>
            </div>
            <p className="text-3xl font-bold text-[#111827]">{stats?.avg_score || 0}</p>
            <p className="text-gray-500 text-sm">Average Score</p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-gray-100" data-testid="stat-best-category">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-[#F59E0B]/20 rounded-xl flex items-center justify-center">
                <Award className="w-5 h-5 text-[#F59E0B]" />
              </div>
            </div>
            <p className="text-3xl font-bold text-[#111827] capitalize">{stats?.best_category || '—'}</p>
            <p className="text-gray-500 text-sm">Best Category</p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-gray-100" data-testid="stat-trend">
            <div className="flex items-center justify-between mb-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                (stats?.improvement_trend || 0) >= 0 ? 'bg-green-100' : 'bg-red-100'
              }`}>
                <TrendingUp className={`w-5 h-5 ${
                  (stats?.improvement_trend || 0) >= 0 ? 'text-green-600' : 'text-red-600 rotate-180'
                }`} />
              </div>
            </div>
            <p className="text-3xl font-bold text-[#111827]">
              {(stats?.improvement_trend || 0) >= 0 ? '+' : ''}{stats?.improvement_trend || 0}
            </p>
            <p className="text-gray-500 text-sm">Improvement Trend</p>
          </div>
        </div>

        {/* Achievements */}
        {achievements && (
          <div data-testid="achievements-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#111827]">Achievements</h3>
              <span className="text-sm text-gray-500">{achievements.earned_count}/{achievements.total_count} unlocked</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {achievements.achievements.map((a) => {
                const Icon = ACHIEVEMENT_ICONS[a.icon] || Award;
                return (
                  <div
                    key={a.id}
                    className={`rounded-2xl p-4 border text-center transition-all ${
                      a.earned
                        ? 'bg-gradient-to-b from-amber-50 to-white border-amber-200 shadow-sm'
                        : 'bg-gray-50 border-gray-100 opacity-50'
                    }`}
                    data-testid={`achievement-${a.id}`}
                  >
                    <div className={`w-10 h-10 mx-auto rounded-xl flex items-center justify-center mb-2 ${
                      a.earned ? 'bg-amber-100' : 'bg-gray-200'
                    }`}>
                      {a.earned ? <Icon className="w-5 h-5 text-amber-600" /> : <Lock className="w-4 h-4 text-gray-400" />}
                    </div>
                    <p className={`text-xs font-semibold mb-0.5 ${a.earned ? 'text-[#111827]' : 'text-gray-400'}`}>{a.title}</p>
                    <p className="text-[10px] text-gray-500 leading-tight">{a.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Progress Chart */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100" data-testid="progress-chart">
          <h3 className="text-lg font-semibold text-[#111827] mb-6">Progress Over Time</h3>
          {stats?.recent_scores?.length > 0 ? (
            <MeasuredChart className="h-64">
              {({ width, height }) => (
              <ResponsiveContainer width={width} height={height} minWidth={1} minHeight={1}>
                <LineChart data={stats.recent_scores}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis 
                    dataKey="session" 
                    stroke="#9CA3AF" 
                    fontSize={12}
                    tickLine={false}
                  />
                  <YAxis 
                    stroke="#9CA3AF" 
                    fontSize={12}
                    tickLine={false}
                    domain={[0, 100]}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      borderRadius: '12px', 
                      border: 'none', 
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="score" 
                    stroke="#00E085" 
                    strokeWidth={3}
                    dot={{ fill: '#00E085', strokeWidth: 0, r: 4 }}
                    activeDot={{ r: 6, fill: '#00E085' }}
                  />
                </LineChart>
              </ResponsiveContainer>
              )}
            </MeasuredChart>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Complete your first session to see progress</p>
              </div>
            </div>
          )}
        </div>

        {/* Available Scenarios */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-[#111827]">Available Scenarios</h3>
            <Link to="/scenarios" className="text-[#00E085] font-medium text-sm hover:underline" data-testid="view-all-scenarios">
              View all
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {scenarios.slice(0, 2).map((scenario) => (
              <Link 
                key={scenario.id} 
                to={`/scenario/${scenario.id}`}
                className="bg-white rounded-2xl p-6 border border-gray-100 card-hover block"
                data-testid={`scenario-card-${scenario.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${categoryColors[scenario.category]}`}>
                    {scenario.category}
                  </span>
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </div>
                <h4 className="font-semibold text-[#111827] mb-2">{scenario.title}</h4>
                <p className="text-gray-600 text-sm line-clamp-2">{scenario.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
